import React, { useEffect, useState } from 'react';
import type { AdAccount, Page, Post, WizardTarget } from './useLaunchWizard.ts';
import { PastScheduleWarning } from './PastScheduleWarning.tsx';

function BulkNamesModal({ rowCount, onAdd, onClose }: { rowCount: number; onAdd: (names: string[]) => void; onClose: () => void }) {
  const [text, setText] = useState('');

  const handleAdd = () => {
    const names = text.split('\n').map((l) => l.trim()).filter(Boolean);
    onAdd(names);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Bulk custom names</h3>
          <button className="btn btn--ghost btn--sm" onClick={onClose}>✕</button>
        </div>
        <p className="text-muted text-sm">
          Paste campaign names — one per line. They will fill the <strong>Custom name</strong> column in order ({rowCount} campaign{rowCount !== 1 ? 's' : ''} selected).
        </p>
        <textarea
          className="input modal-textarea"
          placeholder={'Campaign_Name_1\nCampaign_Name_2\n...'}
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoFocus
        />
        <div className="modal-actions">
          <button className="btn btn--secondary btn--sm" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary btn--sm" onClick={handleAdd}>Add</button>
        </div>
      </div>
    </div>
  );
}

export function AdsStep({
  targets,
  accounts,
  pages,
  posts,
  loadPosts, syncPosts, syncingPosts, postErrors,
  updateTarget,
  adsetsCount,
  setAdsetsCount,
  adsPerAdset,
  setAdsPerAdset,
  selectedOrder,
  scheduledAt,
  tzOffsetHours,
}: {
  targets: Record<string, WizardTarget[]>;
  accounts: AdAccount[];
  pages: Page[];
  posts: Record<string, Post[]>;
  loadPosts: (page: string) => void;
  syncPosts: (page: string) => Promise<void>;
  syncingPosts: Record<string, boolean>;
  postErrors: Record<string, string>;
  updateTarget: (accountId: string, localId: string, patch: Partial<WizardTarget>) => void;
  adsetsCount: number;
  setAdsetsCount: (n: number) => void;
  adsPerAdset: number;
  setAdsPerAdset: (n: number) => void;
  selectedOrder: string[];
  scheduledAt: string | null;
  tzOffsetHours: number;
}) {
  const [showBulkNames, setShowBulkNames] = useState(false);

  // Flat ordered list of { accountId, target } pairs — preserves account order and campaign order within each account
  const selected = selectedOrder.flatMap((accountId) =>
    (targets[accountId] ?? []).map((target) => ({ accountId, target }))
  );

  useEffect(() => {
    selected.forEach(({ target }) => target.page_id && loadPosts(target.page_id));
  }, [selected.map(({ target }) => target.page_id).join(',')]);

  // Convert ISO string to datetime-local value using user's chosen timezone
  const toDatetimeLocal = (iso: string | null): string => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const offset = tzOffsetHours * 60; // in minutes
      const local = new Date(d.getTime() + offset * 60000);
      return local.toISOString().slice(0, 16);
    } catch {
      return '';
    }
  };

  // Convert datetime-local value back to ISO string using user's chosen timezone
  const fromDatetimeLocal = (val: string): string => {
    const d = new Date(val + 'Z');
    const utcMs = d.getTime() - (tzOffsetHours * 3600000);
    return new Date(utcMs).toISOString();
  };

  // Bulk names are now per-campaign row (not per-account)
  const handleBulkNames = (names: string[]) => {
    selected.forEach(({ accountId, target }, i) => {
      const name = names[i]?.trim() ?? '';
      updateTarget(accountId, target.local_id, { custom_name: name });
    });
  };

  // Apply post to all rows
  const applyPostToAll = (postId: string) => {
    selected.forEach(({ accountId, target }) => {
      updateTarget(accountId, target.local_id, { post_id: postId });
    });
  };

  // Collect all unique page_ids from selected rows so we can show a combined post list in "Apply to all"
  const allPageIds = Array.from(new Set(selected.map(({ target }) => target.page_id).filter(Boolean)));
  const allAvailablePosts = Array.from(
    new Map(
      allPageIds.flatMap((pageId) => (posts[pageId] ?? []).map((post) => [post.id, post]))
    ).values()
  );

  return (
    <div className="wizard-step">
      {showBulkNames && (
        <BulkNamesModal
          rowCount={selected.length}
          onAdd={handleBulkNames}
          onClose={() => setShowBulkNames(false)}
        />
      )}

      <div className="inline-fields">
        <label className="field">
          <span className="label">Ad sets</span>
          <input className="input" min="1" type="number" value={adsetsCount} onChange={(e) => setAdsetsCount(Math.max(1, Number(e.target.value)))} />
        </label>
        <label className="field">
          <span className="label">Ads per ad set</span>
          <input className="input" min="1" type="number" value={adsPerAdset} onChange={(e) => setAdsPerAdset(Math.max(1, Number(e.target.value)))} />
        </label>
        <button className="btn btn--outline btn--sm" style={{ alignSelf: 'flex-end' }} onClick={() => setShowBulkNames(true)}>⚡ Bulk Names</button>
      </div>
      <p className="text-muted text-sm">N-M-K: 1 campaign, {adsetsCount} ad sets, {adsPerAdset} ads in each.</p>

      <div className="wizard-table">
        <div className="wizard-row wizard-row--ads wizard-row--header">
          <span>Ad account</span>
          <span>Fan Page</span>
          <span>Custom name</span>
          <span>Start time (UTC{tzOffsetHours >= 0 ? '+' : ''}{tzOffsetHours})</span>
          <span>Post</span>
        </div>

        {/* Apply-to-all row for Post column */}
        {selected.length > 0 && (
          <div className="wizard-row wizard-row--ads wizard-row--apply-all">
            <div className="text-muted text-sm" style={{ fontStyle: 'italic' }}>Apply to all</div>
            <div></div>
            <div></div>
            <div></div>
            <select
              className="input"
              value=""
              onChange={(e) => { if (e.target.value) applyPostToAll(e.target.value); }}
            >
              <option value="">— Post for all —</option>
              {allAvailablePosts.map((post) => (
                <option key={post.id} value={post.id} title={post.message ?? ''}>
                  {(post.message ?? 'Untitled post').slice(0, 30)}
                </option>
              ))}
            </select>
          </div>
        )}

        {selected.map(({ accountId, target }, i) => {
          // Show full account name only for the first row of each account
          const isFirstForAccount = i === 0 || selected[i - 1].accountId !== accountId;
          const account = accounts.find((a) => a.id === accountId);
          return (
            <div className="wizard-row wizard-row--ads" key={target.local_id}>
              <div>
                {isFirstForAccount ? (
                  <>
                    <strong>{account?.name ?? accountId}</strong>
                    <small>{accountId}</small>
                  </>
                ) : (
                  <small className="text-muted">↳ Campaign {(targets[accountId] ?? []).findIndex((r) => r.local_id === target.local_id) + 1}</small>
                )}
              </div>
              <div>{pages.find((p) => p.id === target.page_id)?.name ?? target.page_id}</div>
              <input
                className="input"
                placeholder="Custom name (optional)"
                value={target.custom_name}
                onChange={(e) => updateTarget(accountId, target.local_id, { custom_name: e.target.value })}
                title="Overrides template macro patterns for campaign, adset, and ad names for this campaign row"
              />
              <div>
              <input
                className="input"
                type="datetime-local"
                value={toDatetimeLocal(target.scheduled_at ?? scheduledAt)}
                onChange={(e) => {
                  const val = e.target.value;
                  updateTarget(accountId, target.local_id, {
                    scheduled_at: val ? fromDatetimeLocal(val) : null,
                  });
                }}
                title="Start time for this campaign (overrides global setting)"
              />
              <PastScheduleWarning dates={[target.scheduled_at ?? scheduledAt]} />
              </div>
              <div className="wizard-post-picker">
              <select className="input" value={target.post_id} onChange={(e) => updateTarget(accountId, target.local_id, { post_id: e.target.value })}>
                <option value="">Select post</option>
                {(posts[target.page_id] ?? []).map((post) => (
                  <option key={post.id} value={post.id} title={post.message ?? ''}>
                    {(post.message ?? 'Untitled post').slice(0, 30)}
                  </option>
                ))}
              </select>
              <button className="btn btn--secondary btn--sm" disabled={!target.page_id || syncingPosts[target.page_id]} onClick={() => void syncPosts(target.page_id)}>
                {syncingPosts[target.page_id] ? 'Loading posts...' : 'Load latest 2 posts'}
              </button>
              {postErrors[target.page_id] && <small role="alert" className="text-danger">{postErrors[target.page_id]}</small>}
              {posts[target.page_id]?.length === 0 && <small className="text-muted">No saved posts. Load the latest posts from Meta.</small>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
